import moment from 'moment';
import { useQuery } from '@apollo/client';
import { customersApi } from '../api';
import { APIRequestSort } from 'global';
import { DealEventActivityFiltersInput } from '@trnsact/trnsact-shared-types';
import { ActivityListFilters, ActivityPageTabs, ActivityType, EventListFilters } from 'modules/dealActivity';

interface Params {
  customerAccountId: string;
  sorting: APIRequestSort;
  filters: ActivityListFilters;
  activityType: ActivityPageTabs;
}

// The API requires both range boundaries, so an open-ended range is padded with far-away dates
const RANGE_MIN_DATE = '1970-01-01T00:00:00.000Z';
const RANGE_MAX_DATE = '2100-01-01T00:00:00.000Z';

const buildEventFilters = ({
  status,
  ownerId,
  opportunityId,
  dateFrom,
  dateTo,
}: EventListFilters): DealEventActivityFiltersInput => ({
  ...(status.length ? { status } : {}),
  ...(ownerId ? { ownerId } : {}),
  ...(opportunityId ? { opportunityId } : {}),
  ...(dateFrom || dateTo
    ? {
        dateRange: {
          from: dateFrom ? moment(dateFrom).startOf('day').toISOString() : RANGE_MIN_DATE,
          to: dateTo ? moment(dateTo).endOf('day').toISOString() : RANGE_MAX_DATE,
        },
      }
    : {}),
});

export function useActivityHubTabDataFetch(params: Params) {
  const { sorting, filters, customerAccountId, activityType } = params;

  const { data, loading, previousData, refetch } = useQuery(customersApi.getCustomerDealActivities, {
    variables: {
      input: {
        sorting,
        filters: {
          ...(filters.search ? { search: filters.search } : {}),
          ...(activityType !== ActivityPageTabs.ALL
            ? { types: [activityType] }
            : { types: Object.values(ActivityType) }),

          ...(activityType === ActivityPageTabs.CALL ? { callFilters: filters.callFilters } : {}),
          ...(activityType === ActivityPageTabs.EVENT ? { eventFilters: buildEventFilters(filters.eventFilters) } : {}),
        },
        customerAccountId,
      },
    },
  });

  const activityList = (data ?? previousData)?.customer?.customerDealActivities?.list ?? [];
  const prevActivityList = previousData?.customer?.customerDealActivities?.list ?? [];

  return {
    loading,
    refetch,
    prevActivityList,
    activityList: activityList ?? prevActivityList,
  };
}
